import React, { useState, useEffect } from "react";
import { Sparkles, ChevronRight, X, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const STEPS = [
  { id: "ingredients", path: "/ingredients", title: "1. Zutaten-Datenbank",
    body: "54 Premium-Zutaten von Wagyu A5 bis Saibling. Sehen Sie wie sich Stückpreise je Einheit (€/kg, €/L, €/Stück) automatisch berechnen." },
  { id: "recipes", path: "/recipes", title: "2. Rezepte & Food-Cost",
    body: "15 5-Sterne-Gerichte mit DB1/DB2-Kalkulation. Schauen Sie sich z.B. Wagyu Beiried an für Food-Cost-Analyse." },
  { id: "engineering", path: "/engineering", title: "3. Menu Engineering",
    body: "Stars / Plowhorses / Puzzles / Dogs. Wagyu Beiried & Salzburger Nockerl sind Stars. Wiener Schnitzel ist Plowhorse." },
  { id: "db2-costs", path: "/settings?tab=costs", title: "4. DB2 & Betriebskosten",
    body: "Hotel-Kostenprofil: Miete €45k, Personal €380k, Energie €18k bei 8.500 Covers/Monat → Cost-per-Cover automatisch im DB2." },
  { id: "cost-profile", path: "/settings?tab=legal", title: "5. Cost-Profile & Legal",
    body: "Sehen Sie Geschäftskostenprofil + DSGVO/AVV-Dokumente automatisch generiert für Ihr Hotel." },
  { id: "hotel", path: "/hotel", title: "6. Hotel & Halbpension",
    body: "3 Zimmertypen (Suite, Doppel, Einzel) mit Hoch-/Nebensaison-Preisen. F&B-Budget-Kalkulator für Halbpension." },
  { id: "waste", path: "/waste", title: "7. Waste Tracking",
    body: "13 Verschwendungseinträge → €2.833/Monat. Wagyu-Spoilage €800, Sauerrahm-MHD €112. Filtern nach Grund." },
  { id: "ai", path: "/ai", title: "8. F&B Intelligence",
    body: "100 AI-Credits inkl. Generieren Sie neue Rezepte oder Cocktails per Klick. Importieren Sie Speisekarten als PDF." },
];

const STORAGE_KEY = "studioops_demo_tour_step";
const DISMISSED_KEY = "studioops_demo_tour_dismissed";

export default function DemoTourButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(() => {
    try { return Math.min(STEPS.length - 1, Math.max(0, Number(localStorage.getItem(STORAGE_KEY) || 0))); }
    catch { return 0; }
  });
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === "1"; } catch { return false; }
  });

  // Visible only for demo accounts
  if (!user || user === false || !user.demo_mode) return null;
  if (dismissed && !open) {
    // Tiny "restart tour" button
    return (
      <button
        onClick={() => { setDismissed(false); setStepIdx(0); setOpen(true); try { localStorage.removeItem(DISMISSED_KEY); } catch { /* ignore */ } }}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-bg-surface border border-brand/30 text-brand hover:bg-brand/10 transition-colors text-xs shadow-glow"
        data-testid="demo-tour-restart"
      >
        <Sparkles size={12} /> Demo-Tour
      </button>
    );
  }

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const persist = (idx) => {
    try { localStorage.setItem(STORAGE_KEY, String(idx)); } catch { /* ignore */ }
  };

  const goTo = (path) => {
    // Strip query if router doesn't like it
    if (path.includes("?")) navigate(path);
    else navigate(path);
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); goTo(step.path); }}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-colors shadow-glow font-medium text-sm"
        data-testid="demo-tour-launch-btn"
      >
        <Sparkles size={14} /> Demo-Tour starten
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 w-[360px] bg-bg-surface border border-brand/30 rounded-2xl shadow-glow p-5 space-y-3" data-testid="demo-tour-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand font-medium flex items-center gap-1.5">
            <Sparkles size={11} /> Demo-Tour · Schritt {stepIdx + 1}/{STEPS.length}
          </div>
          <h3 className="text-sm font-semibold text-ink-primary mt-1">{step.title}</h3>
        </div>
        <button onClick={() => { setOpen(false); setDismissed(true); try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ } }} className="text-ink-muted hover:text-ink-primary" data-testid="demo-tour-close">
          <X size={14} />
        </button>
      </div>
      <p className="text-xs text-ink-secondary leading-relaxed">{step.body}</p>
      <div className="flex gap-1.5 pt-1">
        {STEPS.map((s, i) => (
          <div key={s.id} className={`flex-1 h-0.5 rounded-full ${i <= stepIdx ? "bg-brand" : "bg-white/10"}`} />
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => { const i = Math.max(0, stepIdx - 1); setStepIdx(i); persist(i); goTo(STEPS[i].path); }}
          disabled={stepIdx === 0}
          className="text-xs text-ink-muted hover:text-ink-primary disabled:opacity-30"
          data-testid="demo-tour-back"
        >Zurück</button>
        {isLast ? (
          <button
            onClick={() => { setOpen(false); setDismissed(true); try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ } }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-brand text-white hover:bg-brand/90"
            data-testid="demo-tour-finish"
          ><Check size={11} /> Fertig</button>
        ) : (
          <button
            onClick={() => { const i = stepIdx + 1; setStepIdx(i); persist(i); goTo(STEPS[i].path); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-brand text-white hover:bg-brand/90"
            data-testid="demo-tour-next"
          >Weiter <ChevronRight size={11} /></button>
        )}
      </div>
    </div>
  );
}
